/**
 * The leaver flow (lifecycle Phase B, 17 Jul) — archiving done properly.
 *
 * Asks for an END DATE (their last day). Live preview shows: bookings after
 * that date (offered for cancellation), and the pro-rata SETTLEMENT — what
 * the employment window entitles them to vs what they've taken, in days and
 * hours/minutes. That balance is the final-pay number: positive = pay it
 * out, negative = they owe leave back.
 *
 * Future end date = a SCHEDULED archive: the settlement applies now (the
 * balance immediately shows what's owed), the daily scheduler archives them
 * the day after their last day, and the badge on the Employees list says so.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, CalendarX, Loader2, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { usersApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { toast } from 'sonner';

const hm = (mins) => {
  if (mins === null || mins === undefined) return '';
  const sign = mins < 0 ? '-' : '';
  const abs = Math.abs(Math.round(mins));
  return `${sign}${Math.floor(abs / 60)}h${abs % 60 ? ` ${abs % 60}m` : ''}`;
};

const Line = ({ label, value, bold, sign }) => (
  <div className={`flex justify-between py-0.5 text-sm ${bold ? 'font-semibold border-t border-border mt-1 pt-1.5' : ''}`}>
    <span className={bold ? '' : 'text-muted-foreground'}>{label}</span>
    <span className={sign === '-' ? 'text-red-500' : sign === '+' ? 'text-green-600' : ''}>
      {sign ? `${sign} ` : ''}{value}
    </span>
  </div>
);

export default function ArchiveEmployeeDialog({ open, onOpenChange, employee, people = [], onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [endDate, setEndDate] = useState(today);
  const [cancelBookings, setCancelBookings] = useState(true);
  const [prorata, setProrata] = useState(true);
  const [preview, setPreview] = useState(null);
  const [successorId, setSuccessorId] = useState('');
  // Separate from the successor choice: the approver fields are plumbing and always
  // move, but reports-to IS the org chart, so moving it is a visible decision.
  const [moveReportingLines, setMoveReportingLines] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setEndDate(employee?.end_date || today);
      setCancelBookings(true);
      setProrata(true);
      setPreview(null);
      setSuccessorId('');
      setMoveReportingLines(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee?.id]);

  // Only people who can actually approve are worth offering — handing over to
  // someone who'd hit a 403 just moves the problem. Same rule as the approver
  // dropdowns on the employee form.
  const APPROVING_ROLES = ['manager', 'admin', 'superadmin'];
  const successorOptions = people.filter((p) =>
    p.id !== employee?.id && p.email && !p.is_archived && APPROVING_ROLES.includes(p.role));

  const deps = preview?.dependencies;
  const dependants = deps
    ? (deps.approves_for?.length || 0) + (deps.reports?.length || 0) + (deps.backup_for?.length || 0)
    : 0;
  const waiting = deps?.pending_approvals || 0;
  // People who only appear under them on the org chart because they manage the
  // department — no reports_to_id involved. Archiving drops those to the top level.
  const viaDept = deps?.org_chart_via_department?.length || 0;
  const needsAttention = dependants > 0 || waiting > 0 || viaDept > 0;
  const flagged = (deps?.departments_managed?.length || 0) + (deps?.policy_levels || 0);

  useEffect(() => {
    if (!open || !employee || !endDate) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await usersApi.archivePreview(employee.id, endDate);
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) setPreview(null);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, employee, endDate]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await usersApi.archiveWithDate(employee.id, {
        end_date: endDate,
        cancel_future_bookings: cancelBookings,
        prorata,
        successor_id: successorId || null,
        move_reporting_lines: moveReportingLines,
      });
      toast.success(r.data.archived
        ? 'Employee archived — find them under "Show leavers"'
        : `Leaving date set — they'll be archived automatically after ${formatDateGB(endDate)}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    } finally {
      setBusy(false);
    }
  };

  if (!employee) return null;
  const s = preview?.settlement;
  const isFuture = endDate > today;
  const owed = s ? s.balance_days : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Archive {employee.first_name} {employee.last_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Their last day of employment</Label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-hive mt-1 block rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="archive-end-date"
            />
            {isFuture && (
              <p className="text-xs text-muted-foreground mt-1">
                A future date schedules the archive — they stay active until then
                and are archived automatically the day after.
              </p>
            )}
          </div>

          {/* Who depends on this person for approvals.
              Archiving used to check only the leaver's own bookings, so a manager
              could be offboarded while six people still pointed at them — and nobody
              found out until a holiday request went nowhere. */}
          {needsAttention && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-600">
                <Users className="w-4 h-4" />
                Other people rely on {employee?.first_name} for approvals
              </div>
              <ul className="text-xs text-muted-foreground list-disc pl-4">
                {deps.approves_for?.length > 0 && (
                  <li>Approves leave for <strong>{deps.approves_for.length}</strong>: {deps.approves_for.map((p) => p.name).join(', ')}</li>
                )}
                {deps.reports?.length > 0 && (
                  <li><strong>{deps.reports.length}</strong> report{deps.reports.length === 1 ? 's' : ''} to them: {deps.reports.map((p) => p.name).join(', ')}</li>
                )}
                {deps.backup_for?.length > 0 && (
                  <li>Backup approver for <strong>{deps.backup_for.length}</strong>: {deps.backup_for.map((p) => p.name).join(', ')}</li>
                )}
                {waiting > 0 && (
                  <li><strong>{waiting}</strong> leave request{waiting === 1 ? ' is' : 's are'} waiting on them right now</li>
                )}
                {viaDept > 0 && (
                  <li>
                    <strong>{viaDept}</strong> {viaDept === 1 ? 'person sits' : 'people sit'} under them on the org chart
                    because they manage the department: {deps.org_chart_via_department.map((p) => p.name).join(', ')}
                  </li>
                )}
              </ul>

              {viaDept > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-500">
                  Those {viaDept === 1 ? 'has' : 'have'} no manager set of their own, so
                  archiving {employee?.first_name} moves {viaDept === 1 ? 'them' : 'them'} to
                  the top of the org chart until you give the department a new manager.
                  Choosing someone below doesn't change that — department managers are set
                  in Departments.
                </p>
              )}

              <div>
                <label className="text-xs font-medium">Who takes this over?</label>
                <select
                  value={successorId}
                  onChange={(e) => setSuccessorId(e.target.value)}
                  className="input-hive mt-1 w-full text-sm"
                  data-testid="successor-picker"
                >
                  <option value="">Nobody — leave it for now</option>
                  {successorOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {successorId
                    ? 'They become the approver for those people, and anything currently waiting moves to them.'
                    : waiting > 0
                      ? `Nothing is lost — the ${waiting} waiting request${waiting === 1 ? '' : 's'} will go to your admins straight away, and future requests fall through to the department manager.`
                      : 'Future requests will fall through to the department manager, then an admin.'}
                </p>

                {/* Reporting lines are the ORG CHART, not approval plumbing. Moving
                    them silently rewrote company structure as a side effect of
                    offboarding — a decision nobody had made. Now it's a tick. */}
                {successorId && deps.reports?.length > 0 && (
                  <label className="flex items-start gap-2 cursor-pointer text-xs mt-2.5">
                    <input
                      type="checkbox"
                      checked={moveReportingLines}
                      onChange={(e) => setMoveReportingLines(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-input accent-primary shrink-0"
                      data-testid="move-reporting-lines"
                    />
                    <span>
                      Also move their {deps.reports.length} team member{deps.reports.length === 1 ? '' : 's'} to
                      report to them on the org chart.
                      {!moveReportingLines && (
                        <span className="text-muted-foreground"> Leaving this off keeps the org chart
                        as it is — you can redraw it yourself later.</span>
                      )}
                    </span>
                  </label>
                )}
              </div>

              {flagged > 0 && (
                <p className="text-xs text-muted-foreground border-t border-amber-500/20 pt-2">
                  They're also
                  {deps.departments_managed?.length > 0 && <> manager of <strong>{deps.departments_managed.map((d) => d.name).join(', ')}</strong></>}
                  {deps.departments_managed?.length > 0 && deps.policy_levels > 0 && ', and'}
                  {deps.policy_levels > 0 && <> named in <strong>{deps.policy_levels}</strong> approval polic{deps.policy_levels === 1 ? 'y' : 'ies'}</>}
                  . Those aren't changed here — sort them in Departments and Settings → Approval Flow when you're ready.
                </p>
              )}
            </div>
          )}

          {/* Bookings after the end date */}
          {preview && preview.bookings_after.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-600 mb-1">
                <CalendarX className="w-4 h-4" />
                {preview.bookings_after.length} booking{preview.bookings_after.length > 1 ? 's' : ''} after their last day
              </div>
              <ul className="text-xs text-muted-foreground mb-2">
                {preview.bookings_after.slice(0, 5).map((b) => (
                  <li key={b.id}>
                    {formatDateGB(b.start_date)}{b.end_date !== b.start_date ? ` – ${formatDateGB(b.end_date)}` : ''} ({b.total_days} day{b.total_days !== 1 ? 's' : ''}, {b.status})
                  </li>
                ))}
                {preview.bookings_after.length > 5 && <li>…and {preview.bookings_after.length - 5} more</li>}
              </ul>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={cancelBookings}
                       onChange={(e) => setCancelBookings(e.target.checked)}
                       className="w-4 h-4 rounded border-input accent-primary" />
                Cancel these and refund them to the balance
              </label>
            </div>
          )}

          {/* The settlement */}
          {s && (
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium mb-2">
                <input type="checkbox" checked={prorata}
                       onChange={(e) => setProrata(e.target.checked)}
                       className="w-4 h-4 rounded border-input accent-primary" />
                Pro-rata their allowance to {formatDateGB(endDate)}
              </label>
              {prorata ? (
                <>
                  <Line label={`Entitled for the year worked (was ${s.granted_full_year})`}
                        value={`${s.granted_to_end_date} days`} />
                  {s.carried_over > 0 && <Line label="Carried over" value={`${s.carried_over} days`} sign="+" />}
                  {(s.bank_holidays_to_end_date_days || 0) > 0 && (
                    <Line label={`Bank holidays up to their last day (${s.bank_holidays_to_end_date_count})`}
                          value={`${s.bank_holidays_to_end_date_days} days`} sign="-" />
                  )}
                  <Line label="Taken / awaiting approval"
                        value={`${(s.used_after_cancellations + s.pending_after_cancellations).toFixed(2).replace(/\.?0+$/, '')} days`} sign="-" />
                  <Line
                    bold
                    label={owed >= 0 ? 'Owed to them in final pay' : 'They owe back'}
                    value={`${Math.abs(owed)} days${s.balance_minutes !== null ? ` (${hm(Math.abs(s.balance_minutes))})` : ''}`}
                  />
                </>
              ) : (
                <p className="text-xs text-muted-foreground flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" />
                  Without pro-rata their balance keeps the full year's allowance —
                  the final-pay figure will overstate what they're owed.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" className="btn-primary" onClick={confirm} disabled={busy} data-testid="archive-confirm">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</>)
                : isFuture ? `Schedule leaving ${formatDateGB(endDate)}` : 'Archive now'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
